import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import AIConsentModal from "@/components/AIConsentModal";
import { api } from "@/lib/api";
import {
  Sparkle, FileText, ArrowsClockwise, Warning, CheckCircle, FileDoc, Target,
  ChatTeardropDots, GraduationCap, ArrowRight, BookOpen, Star, PencilSimple, Trash,
  PlusCircle, MagnifyingGlass, FloppyDisk,
} from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === "true";
const SECTION_KEYS = ["cv", "cover", "interview"];
const CV_SUBS = ["master", "tailored", "scratch", "improve", "saved"];
const COVER_SUBS = ["master", "tailored", "scratch", "improve", "saved"];

function handleAIError(e, fallback = "Something went wrong") {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail || fallback;
  if (status === 402 && BILLING_ENABLED) {
    toast.error(
      <span className="flex flex-col gap-2">
        <span><b>{detail}</b></span>
        <Link to="/billing" className="underline text-sm font-medium flex items-center gap-1">
          see plans <ArrowRight size={12} weight="bold"/>
        </Link>
      </span>,
      { duration: 8000 },
    );
  } else {
    toast.error(detail);
  }
}

// Persist an AI output into Document Safe (saved_letters collection).
async function saveToSafe({ kind, content, title, job_title, company, question }) {
  try {
    await api.post("/saved-letters", { kind, content, title, job_title, company, question });
    toast.success(`saved to document safe — ${title || kind}`);
    return true;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "couldn't save — try again");
    return false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared: Saved list (used by both CV and Cover Letter "saved" sub-actions)
// ─────────────────────────────────────────────────────────────────────────────
function SavedList({ kinds, emptyLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [renameTo, setRenameTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/saved-letters?kind=${kinds.join(",")}`);
      setItems(data);
    } catch { toast.error("couldn't load saved documents"); }
    finally { setLoading(false); }
  }, [kinds]);
  useEffect(() => { load(); }, [load]);

  const rename = async (id) => {
    const t = renameTo.trim();
    if (t.length < 2) { toast.error("give it a real name"); return; }
    try {
      await api.patch(`/saved-letters/${id}`, { title: t });
      toast.success("renamed");
      setEditing(null); setRenameTo("");
      load();
    } catch { toast.error("rename failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this saved document?")) return;
    try { await api.delete(`/saved-letters/${id}`); toast.success("deleted"); load(); }
    catch { toast.error("delete failed"); }
  };
  const promote = async (id) => {
    try {
      await api.post(`/saved-letters/${id}/promote-to-master`);
      toast.success("promoted to master — it now leads the pack ⭐");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "promote failed"); }
  };

  if (loading) return <p className="text-sm text-[var(--brand-muted)]" data-testid="saved-loading">loading…</p>;
  if (items.length === 0) return <p className="text-sm text-[var(--brand-muted)]" data-testid="saved-empty">{emptyLabel}</p>;

  return (
    <div className="space-y-3" data-testid="saved-list">
      {items.map((it) => {
        const isMaster = it.kind.endsWith("_master");
        return (
          <div key={it.id} className={`card-hard rounded-md p-4 ${isMaster ? "bg-[var(--pastel-yellow)]" : "bg-white"}`} data-testid={`saved-${it.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="label-tiny">{isMaster ? "⭐ MASTER" : "📄 TAILORED"}</span>
                  {(it.company || it.job_title) && (
                    <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">
                      {[it.job_title, it.company].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </div>
                {editing === it.id ? (
                  <div className="flex gap-2 items-center">
                    <input value={renameTo} onChange={(e) => setRenameTo(e.target.value)}
                      data-testid={`rename-input-${it.id}`}
                      className="card-hard rounded-md px-2 py-1 text-sm flex-1"/>
                    <button onClick={() => rename(it.id)} data-testid={`rename-save-${it.id}`}
                      className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-3 py-1 text-xs">lock it in</button>
                    <button onClick={() => { setEditing(null); setRenameTo(""); }}
                      className="btn-hard bg-white rounded-md px-3 py-1 text-xs">cancel</button>
                  </div>
                ) : (
                  <p className="font-medium text-sm truncate" title={it.title}>{it.title || "(untitled)"}</p>
                )}
                <p className="text-xs text-[var(--brand-muted)] mt-1 font-mono-d">
                  {(it.word_count || 0)} words · {new Date(it.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-1 flex-wrap">
                <button onClick={() => { setEditing(it.id); setRenameTo(it.title || ""); }}
                  data-testid={`rename-${it.id}`}
                  className="btn-hard bg-white rounded-md px-2 py-1 text-xs flex items-center gap-1">
                  <PencilSimple size={12}/> rename
                </button>
                {!isMaster && (it.kind === "cv_tailored" || it.kind === "cover_letter") && (
                  <button onClick={() => promote(it.id)} data-testid={`promote-${it.id}`}
                    className="btn-hard bg-[var(--pastel-mint)] rounded-md px-2 py-1 text-xs flex items-center gap-1"
                    title="mark this as your master">
                    <Star size={12}/> promote
                  </button>
                )}
                <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`}
                  className="btn-hard bg-white rounded-md px-2 py-1 text-xs flex items-center gap-1">
                  <Trash size={12}/> delete
                </button>
              </div>
            </div>
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-mono-d text-[var(--brand-muted)]">read →</summary>
              <pre className="mt-2 whitespace-pre-wrap text-sm font-sans p-3 bg-[var(--brand-surface-soft,#F7F5EE)] border-[1.5px] border-[var(--brand-ink)] rounded-md" data-testid={`saved-content-${it.id}`}>{it.content}</pre>
            </details>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CV — Tailored (existing tailor-resume flow, now with Save to Safe)
// ─────────────────────────────────────────────────────────────────────────────
function TailoredCV() {
  const [jd, setJd] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [targetCompany, setTargetCompany] = useState("");
  const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [exporting, setExporting] = useState(false);

  const run = async () => {
    if (jd.trim().length < 50) { toast.error("Paste a longer job description (50+ chars)"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/tailor-resume", { job_description: jd, target_role: targetRole, target_company: targetCompany, focus });
      setResult(data);
      toast.success("tailored — scroll down");
    } catch (e) { handleAIError(e, "tailoring failed"); }
    finally { setRunning(false); }
  };
  const downloadDocx = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_BASE}/api/ai/tailored-docx`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_description: jd, target_role: targetRole, target_company: targetCompany, focus }),
      });
      if (!res.ok) { toast.error("download failed"); return; }
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `tailored-cv-${Date.now()}.docx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("tailored CV downloaded");
    } catch { toast.error("download failed"); }
    finally { setExporting(false); }
  };
  const persist = async () => {
    if (!result) return;
    const tp = result.tailored_profile || {};
    const meta = result.meta || {};
    const parts = [];
    if (meta.tailored_headline) parts.push(meta.tailored_headline);
    if (meta.tailored_summary) parts.push("\n" + meta.tailored_summary);
    (tp.work_experience || []).forEach((w) => {
      parts.push(`\n${w.role} · ${w.employer}\n${w.responsibilities || ""}`);
    });
    const content = parts.join("\n").trim();
    if (content.length < 20) { toast.error("nothing to save"); return; }
    await saveToSafe({
      kind: "cv_tailored", content,
      title: `${targetRole || "Tailored CV"}${targetCompany ? ` — ${targetCompany}` : ""}`,
      job_title: targetRole, company: targetCompany,
    });
  };

  const meta = result?.meta || {};
  const tp = result?.tailored_profile || {};
  const work = tp.work_experience || [];

  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/tailored cv generator</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">paste the JD, get a CV that speaks its language.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">
          pulls straight from your vault. rewrites bullets to match the role. never invents skills or jobs you don&apos;t have.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="tailor-role" placeholder="target role" value={targetRole} onChange={(e) => setTargetRole(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
          <input data-testid="tailor-company" placeholder="target company (optional)" value={targetCompany} onChange={(e) => setTargetCompany(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
        </div>
        <textarea data-testid="tailor-jd" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="paste the full job description" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[160px] text-sm mb-3"/>
        <input data-testid="tailor-focus" placeholder="optional steer" value={focus} onChange={(e) => setFocus(e.target.value)} className="w-full card-hard rounded-md px-3 py-2 bg-white mb-3"/>
        <div className="flex gap-2 flex-wrap">
          <button onClick={run} disabled={running} data-testid="tailor-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            {running ? <><ArrowsClockwise size={16} className="animate-spin"/> tailoring…</> : <><Sparkle size={16} weight="bold"/> tailor my vault</>}
          </button>
          {result && (
            <>
              <button onClick={downloadDocx} disabled={exporting} data-testid="tailor-download-docx" className="btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
                {exporting ? <ArrowsClockwise size={16} className="animate-spin"/> : <FileDoc size={16} weight="bold"/>}
                {exporting ? "rendering…" : "download .docx"}
              </button>
              <button onClick={persist} data-testid="tailor-save" className="btn-hard bg-[var(--pastel-mint)] text-[var(--brand-ink)] rounded-md px-4 py-2.5 text-sm flex items-center gap-2">
                <FloppyDisk size={16} weight="bold"/> save to document safe
              </button>
            </>
          )}
        </div>
      </div>
      {result && (
        <div className="space-y-6" data-testid="tailor-result">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)] flex items-center gap-6 flex-wrap">
            <div>
              <p className="label-tiny mb-1.5">/match score</p>
              <div className="font-display text-5xl font-semibold tracking-tight" data-testid="tailor-match-score">{meta.match_score ?? 0}%</div>
            </div>
            <div className="flex-1 min-w-[260px]">
              <p className="label-tiny mb-1">tailored headline</p>
              <p className="text-base font-medium" data-testid="tailor-headline">{meta.tailored_headline || "—"}</p>
              <p className="label-tiny mb-1 mt-3">why this approach</p>
              <p className="text-sm text-[var(--brand-muted)]">{meta.rationale || ""}</p>
            </div>
          </div>
          <div className="card-hard rounded-md p-5 bg-white" data-testid="tailor-preview">
            <p className="label-tiny mb-3">/tailored summary</p>
            <p className="text-sm italic mb-4">&ldquo;{meta.tailored_summary || (tp.personal_info?.summary || "")}&rdquo;</p>
            <p className="label-tiny mb-2">/tailored work bullets</p>
            <div className="space-y-4">
              {work.map((w) => (
                <div key={w.id} className="border-l-[3px] border-[var(--brand-primary)] pl-3" data-testid={`tailor-work-${w.id}`}>
                  <p className="font-medium text-sm">{w.role} · {w.employer}</p>
                  <pre className="font-sans text-sm text-[var(--brand-muted)] whitespace-pre-wrap mt-1">{w.responsibilities}</pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CV — Master (one-click generate a Master CV from the whole vault)
// ─────────────────────────────────────────────────────────────────────────────
const MASTER_JD_STUB = "MASTER CV — generate a role-agnostic, achievement-forward CV that best represents this candidate for a wide range of relevant roles. Emphasise transferable strengths and quantified impact, using ONLY evidence present in the vault.";
function MasterCV() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saved-letters?kind=cv_master");
      setExisting(data[0] || null);
    } catch { /* fine */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/tailor-resume", { job_description: MASTER_JD_STUB });
      setResult(data);
      const tp = data.tailored_profile || {};
      const meta = data.meta || {};
      const parts = [];
      if (meta.tailored_headline) parts.push(meta.tailored_headline);
      if (meta.tailored_summary) parts.push("\n" + meta.tailored_summary);
      (tp.work_experience || []).forEach((w) => parts.push(`\n${w.role} · ${w.employer}\n${w.responsibilities || ""}`));
      const content = parts.join("\n").trim();
      if (content.length >= 20) {
        await saveToSafe({ kind: "cv_master", content, title: "Master CV" });
        load();
      }
    } catch (e) { handleAIError(e, "master cv generation failed"); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)] mb-6">
        <p className="label-tiny mb-2">/master cv ⭐</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">one CV to represent you everywhere.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">
          uses every field in your vault to build a role-agnostic, achievement-forward CV. this becomes the base for every tailored version.
        </p>
        <button onClick={generate} disabled={running} data-testid="master-cv-generate" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <><ArrowsClockwise size={16} className="animate-spin"/> generating…</> : <><Sparkle size={16} weight="bold"/> {existing ? "regenerate master cv" : "generate master cv"}</>}
        </button>
      </div>
      {loading && <p className="text-sm text-[var(--brand-muted)]">loading…</p>}
      {existing && !result && (
        <div className="card-hard rounded-md p-5 bg-white" data-testid="master-cv-existing">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="label-tiny">⭐ current master</span>
            <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">{new Date(existing.created_at).toLocaleDateString()} · {existing.word_count} words</span>
          </div>
          <pre className="whitespace-pre-wrap text-sm font-sans" data-testid="master-cv-content">{existing.content}</pre>
        </div>
      )}
      {!loading && !existing && !result && (
        <p className="text-sm text-[var(--brand-muted)]" data-testid="master-cv-empty">no master cv yet — hit the button above.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CV — From Scratch (generate from a role brief when vault is thin)
// ─────────────────────────────────────────────────────────────────────────────
function CVFromScratch() {
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [jd, setJd] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (role.trim().length < 2) { toast.error("give it a target role first"); return; }
    if (jd.trim().length < 50) { toast.error("paste a JD (50+ chars) so we know what to build"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/tailor-resume", { job_description: jd, target_role: role, target_company: company, focus: "SCRATCH MODE: heavily emphasise transferable skills; be honest about experience gaps." });
      setResult(data); toast.success("draft ready — save it to the safe when you're happy");
    } catch (e) { handleAIError(e, "generation failed"); }
    finally { setRunning(false); }
  };
  const persist = async () => {
    if (!result) return;
    const tp = result.tailored_profile || {}; const meta = result.meta || {};
    const parts = [];
    if (meta.tailored_headline) parts.push(meta.tailored_headline);
    if (meta.tailored_summary) parts.push("\n" + meta.tailored_summary);
    (tp.work_experience || []).forEach((w) => parts.push(`\n${w.role} · ${w.employer}\n${w.responsibilities || ""}`));
    await saveToSafe({ kind: "cv_tailored", content: parts.join("\n").trim(), title: `${role} — from scratch`, job_title: role, company });
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/cv from scratch</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">no cv yet? start here.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">give us a role + JD and we&apos;ll draft a first version from whatever&apos;s already in your vault — even sparse profiles get a starting point.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="scratch-role" placeholder="target role" value={role} onChange={(e) => setRole(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
          <input data-testid="scratch-company" placeholder="target company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
        </div>
        <textarea data-testid="scratch-jd" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="paste the JD" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[140px] text-sm mb-3"/>
        <div className="flex gap-2 flex-wrap">
          <button onClick={run} disabled={running} data-testid="scratch-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            {running ? <ArrowsClockwise size={16} className="animate-spin"/> : <PlusCircle size={16} weight="bold"/>} draft cv
          </button>
          {result && <button onClick={persist} data-testid="scratch-save" className="btn-hard bg-[var(--pastel-mint)] rounded-md px-4 py-2.5 text-sm flex items-center gap-2"><FloppyDisk size={16} weight="bold"/> save to document safe</button>}
        </div>
      </div>
      {result && (
        <pre className="card-hard rounded-md p-5 bg-white whitespace-pre-wrap text-sm font-sans" data-testid="scratch-result">
{result.meta?.tailored_headline}
{"\n\n"}{result.meta?.tailored_summary}
{"\n\n"}{(result.tailored_profile?.work_experience || []).map((w) => `${w.role} · ${w.employer}\n${w.responsibilities || ""}`).join("\n\n")}
        </pre>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CV — Improve / Review (paste an existing CV → get structured feedback)
// ─────────────────────────────────────────────────────────────────────────────
function ImproveCV() {
  const [content, setContent] = useState("");
  const [jd, setJd] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (content.trim().length < 100) { toast.error("paste your CV (100+ chars)"); return; }
    setRunning(true); setResult(null);
    try {
      // Reuse the tailor endpoint — the pasted CV goes into `focus` as "existing CV to improve".
      const { data } = await api.post("/ai/tailor-resume", {
        job_description: jd.trim().length >= 50 ? jd : MASTER_JD_STUB,
        focus: `IMPROVEMENT MODE: The candidate has pasted their EXISTING CV below. Give feedback on how to strengthen it and rewrite key sections to be sharper and more evidence-backed. EXISTING CV:\n${content.slice(0, 6000)}`,
      });
      setResult(data); toast.success("review ready");
    } catch (e) { handleAIError(e, "review failed"); }
    finally { setRunning(false); }
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/improve · review existing cv</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">bring a cv, get a critique + rewrite.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">paste an existing CV; optionally add a target JD for role-specific improvements.</p>
        <textarea data-testid="improve-cv-content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="paste your current cv here" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[200px] text-sm mb-3"/>
        <textarea data-testid="improve-cv-jd" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="optional — paste a JD for targeted improvements" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[100px] text-sm mb-3"/>
        <button onClick={run} disabled={running} data-testid="improve-cv-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <ArrowsClockwise size={16} className="animate-spin"/> : <MagnifyingGlass size={16} weight="bold"/>} review my cv
        </button>
      </div>
      {result && (
        <div className="space-y-4" data-testid="improve-cv-result">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)]">
            <p className="label-tiny mb-2">/rewritten summary</p>
            <p className="text-sm italic">&ldquo;{result.meta?.tailored_summary || "—"}&rdquo;</p>
          </div>
          {result.meta?.skills_to_emphasise?.length > 0 && (
            <div className="card-hard rounded-md p-4 bg-[var(--pastel-yellow)]">
              <p className="label-tiny mb-2 flex items-center gap-1.5"><CheckCircle size={12} weight="duotone"/> /surface these strengths</p>
              <div className="flex flex-wrap gap-1.5">{result.meta.skills_to_emphasise.map((s) => <span key={s} className="card-hard rounded-full px-2 py-0.5 text-xs bg-white">{s}</span>)}</div>
            </div>
          )}
          {result.meta?.skills_gaps?.length > 0 && (
            <div className="card-hard rounded-md p-4 bg-[var(--pastel-peach)]">
              <p className="label-tiny mb-2 flex items-center gap-1.5"><Warning size={12} weight="duotone"/> /honest gaps</p>
              <ul className="text-sm space-y-1">{result.meta.skills_gaps.map((s, i) => <li key={`gap-${s || i}`}>· {s}</li>)}</ul>
            </div>
          )}
          <div className="card-hard rounded-md p-4 bg-white">
            <p className="label-tiny mb-2">/rationale</p>
            <p className="text-sm">{result.meta?.rationale}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cover Letter — Master (empty JD → generic master letter)
// ─────────────────────────────────────────────────────────────────────────────
function MasterCoverLetter() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/saved-letters?kind=cover_letter_master"); setExisting(data[0] || null); }
    catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const generate = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/cover-letter-generate", {
        job_description: "", job_title: "", company: "",
        tone: "professional", length: "medium",
        focus: "MASTER LETTER — write a role-agnostic letter the candidate can adapt for any application. Emphasise transferable strengths from the vault.",
      });
      setResult(data);
      await saveToSafe({ kind: "cover_letter_master", content: data.cover_letter, title: "Master Cover Letter" });
      load();
    } catch (e) { handleAIError(e, "master cover letter generation failed"); }
    finally { setRunning(false); }
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-[var(--pastel-yellow)] mb-6">
        <p className="label-tiny mb-2">/master cover letter ⭐</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">a base letter you tweak per role.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">pulls from your vault. no JD needed. becomes the starting point for every tailored letter.</p>
        <button onClick={generate} disabled={running} data-testid="master-cl-generate" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <><ArrowsClockwise size={16} className="animate-spin"/> writing…</> : <><Sparkle size={16} weight="bold"/> {existing ? "regenerate master letter" : "generate master letter"}</>}
        </button>
      </div>
      {loading && <p className="text-sm text-[var(--brand-muted)]">loading…</p>}
      {(result || existing) && (
        <div className="card-hard rounded-md p-5 bg-white" data-testid="master-cl-existing">
          <p className="label-tiny mb-2">⭐ current master</p>
          <pre className="whitespace-pre-wrap text-sm font-sans" data-testid="master-cl-content">{result?.cover_letter || existing?.content}</pre>
        </div>
      )}
      {!loading && !existing && !result && (
        <p className="text-sm text-[var(--brand-muted)]" data-testid="master-cl-empty">no master letter yet — hit generate above.</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cover Letter — Tailored (per-role, save to safe)
// ─────────────────────────────────────────────────────────────────────────────
function TailoredCoverLetter() {
  const [jd, setJd] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (jd.trim().length < 50 && !jobTitle) { toast.error("give us a role or JD to tailor to"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/cover-letter-generate", { job_description: jd, job_title: jobTitle, company, tone, length });
      setResult(data); toast.success("letter drafted");
    } catch (e) { handleAIError(e, "generation failed"); }
    finally { setRunning(false); }
  };
  const persist = async () => {
    if (!result) return;
    await saveToSafe({ kind: "cover_letter", content: result.cover_letter, title: `${jobTitle || "Cover Letter"}${company ? ` — ${company}` : ""}`, job_title: jobTitle, company });
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/tailored cover letter generator</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">one letter, one role.</h3>
        <p className="text-sm text-[var(--brand-muted)] mb-4">draft a role-specific cover letter using your vault + the JD. save the winners to your safe.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="tcl-role" placeholder="job title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
          <input data-testid="tcl-company" placeholder="company" value={company} onChange={(e) => setCompany(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
        </div>
        <textarea data-testid="tcl-jd" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="paste the job description" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[140px] text-sm mb-3"/>
        <div className="flex gap-2 mb-3">
          <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="tcl-tone" className="card-hard rounded-md px-3 py-2 bg-white text-sm flex-1">
            <option value="professional">professional</option>
            <option value="warm">warm</option>
            <option value="confident">confident</option>
            <option value="candid">candid</option>
          </select>
          <select value={length} onChange={(e) => setLength(e.target.value)} data-testid="tcl-length" className="card-hard rounded-md px-3 py-2 bg-white text-sm flex-1">
            <option value="short">short (~150 words)</option>
            <option value="medium">medium (~250 words)</option>
            <option value="long">long (~400 words)</option>
          </select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={run} disabled={running} data-testid="tcl-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
            {running ? <ArrowsClockwise size={16} className="animate-spin"/> : <FileText size={16} weight="bold"/>} draft letter
          </button>
          {result && <button onClick={persist} data-testid="tcl-save" className="btn-hard bg-[var(--pastel-mint)] rounded-md px-4 py-2.5 text-sm flex items-center gap-2"><FloppyDisk size={16} weight="bold"/> save to document safe</button>}
        </div>
      </div>
      {result && (
        <div className="card-hard rounded-md p-5 bg-white" data-testid="tcl-result">
          <pre className="whitespace-pre-wrap text-sm font-sans">{result.cover_letter}</pre>
          <p className="text-xs font-mono-d text-[var(--brand-muted)] mt-3">{result.meta?.word_count} words · {result.meta?.tone}</p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cover Letter — From Scratch (blank prompt → guided draft)
// ─────────────────────────────────────────────────────────────────────────────
function CLFromScratch() {
  return (
    <div className="card-hard rounded-md p-5 bg-white">
      <p className="label-tiny mb-2">/cover letter from scratch</p>
      <h3 className="font-display text-xl tracking-tight mb-2">no draft yet? start here.</h3>
      <p className="text-sm text-[var(--brand-muted)] mb-3">the tailored generator will build one from scratch — just leave the &quot;paste your draft&quot; field on Improve/Review empty. or use the Tailored tab with a full JD.</p>
      <TailoredCoverLetter/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cover Letter — Improve / Review (was: "Cover Letter Checker")
// ─────────────────────────────────────────────────────────────────────────────
function ImproveCoverLetter() {
  const [cover, setCover] = useState("");
  const [jd, setJd] = useState("");
  const [usePersonal, setUsePersonal] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (cover.trim().length < 50) { toast.error("paste a longer cover letter (50+ chars)"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/ai/cover-letter-check", { cover_letter: cover, job_description: jd, use_personal_examples: usePersonal });
      setResult(data); toast.success("review complete");
    } catch (e) { handleAIError(e, "review failed"); }
    finally { setRunning(false); }
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/improve · review cover letter</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">ATS keyword match · AI-sounding score · rewrites.</h3>
        <textarea data-testid="cover-input" value={cover} onChange={(e) => setCover(e.target.value)} placeholder="paste your draft" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[180px] text-sm mb-4"/>
        <textarea data-testid="jd-input" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="optional — paste the JD" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[100px] text-sm mb-3"/>
        <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
          <input type="checkbox" checked={usePersonal} onChange={(e) => setUsePersonal(e.target.checked)} data-testid="use-personal"/>
          <span>use my Personal Bank for rewrite suggestions</span>
        </label>
        <button onClick={run} disabled={running} data-testid="cover-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <ArrowsClockwise size={16} className="animate-spin"/> : <MagnifyingGlass size={16} weight="bold"/>} review
        </button>
      </div>
      {result && (
        <div className="space-y-4" data-testid="cover-result">
          <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)]">
            <p className="label-tiny mb-2">/ats keyword match</p>
            <div className="font-display text-5xl font-semibold tracking-tight" data-testid="ats-score">{result.ats?.match_percent ?? "—"}%</div>
            {result.ats?.missing_keywords?.length > 0 && (
              <div className="mt-3">
                <p className="label-tiny mb-1">missing keywords:</p>
                <div className="flex flex-wrap gap-1.5">{result.ats.missing_keywords.map((kw) => <span key={kw} className="card-hard rounded-full px-2 py-0.5 text-xs bg-white">{kw}</span>)}</div>
              </div>
            )}
          </div>
          {result.ai?.suggested_rewrites?.length > 0 && (
            <div className="card-hard rounded-md p-5 bg-white">
              <p className="label-tiny mb-2">/suggested rewrites</p>
              <div className="space-y-3">
                {result.ai.suggested_rewrites.map((r, i) => (
                  <div key={i} className="border-l-[3px] border-[var(--brand-primary)] pl-4">
                    <p className="text-xs text-[var(--brand-muted)] italic mb-1">&ldquo;{r.original}&rdquo;</p>
                    <p className="text-sm font-medium">{r.improved}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Interview Prep (unchanged behaviour — one panel, 4 outputs)
// ─────────────────────────────────────────────────────────────────────────────
function InterviewPrepPanel() {
  const [role, setRole] = useState(""); const [company, setCompany] = useState("");
  const [jd, setJd] = useState(""); const [focus, setFocus] = useState("");
  const [running, setRunning] = useState(false); const [result, setResult] = useState(null);
  const run = async () => {
    if (role.trim().length < 2) { toast.error("target role first"); return; }
    setRunning(true); setResult(null);
    try { const { data } = await api.post("/ai/interview-prep", { role, company, job_description: jd, focus }); setResult(data); toast.success("prep ready"); }
    catch (e) { handleAIError(e, "couldn't build session"); }
    finally { setRunning(false); }
  };
  return (
    <div>
      <div className="card-hard rounded-md p-5 bg-white mb-6">
        <p className="label-tiny mb-2">/interview prep · ai coach · mock questions · STAR · role-specific</p>
        <h3 className="font-display text-2xl tracking-tight mb-2">your vault, in question form.</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input data-testid="interview-role" placeholder="target role" value={role} onChange={(e) => setRole(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
          <input data-testid="interview-company" placeholder="target company (optional)" value={company} onChange={(e) => setCompany(e.target.value)} className="card-hard rounded-md px-3 py-2 bg-white"/>
        </div>
        <textarea data-testid="interview-jd" value={jd} onChange={(e) => setJd(e.target.value)} placeholder="optional JD" className="w-full card-hard rounded-md px-3 py-3 bg-white min-h-[100px] text-sm mb-3"/>
        <input data-testid="interview-focus" placeholder="optional focus (e.g. behavioural, STAR, stakeholder management)" value={focus} onChange={(e) => setFocus(e.target.value)} className="w-full card-hard rounded-md px-3 py-2 bg-white mb-3"/>
        <button onClick={run} disabled={running} data-testid="interview-run" className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-60">
          {running ? <ArrowsClockwise size={16} className="animate-spin"/> : <ChatTeardropDots size={16} weight="bold"/>} build prep session
        </button>
      </div>
      {result && (
        <div className="space-y-5" data-testid="interview-result">
          {result.summary && <div className="card-hard rounded-md p-5 bg-[var(--pastel-peach)]"><p className="label-tiny mb-2">/session focus</p><p className="text-sm">{result.summary}</p></div>}
          {(result.questions || []).length > 0 && (
            <div className="space-y-3" data-testid="interview-questions">
              {result.questions.map((q, i) => (
                <details key={`q-${(q.question || "").slice(0, 40)}-${i}`} className="card-hard rounded-md p-4 bg-white" data-testid={`interview-q-${i}`}>
                  <summary className="cursor-pointer font-medium flex items-start gap-3 list-none">
                    <span className="font-mono-d text-xs text-[var(--brand-muted)] mt-0.5 shrink-0">Q{i + 1}</span>
                    <span className="flex-1">{q.question}</span>
                    <span className="label-tiny px-2 py-0.5 rounded-full bg-[var(--pastel-yellow)] shrink-0 text-[10px]">{q.type || "general"}</span>
                  </summary>
                  <div className="mt-4 space-y-3 text-sm pl-9">
                    {q.why_asked && <div><p className="label-tiny mb-1">/why they ask</p><p className="text-[var(--brand-muted)]">{q.why_asked}</p></div>}
                    {q.approach && <div><p className="label-tiny mb-1">/your approach</p><p>{q.approach}</p></div>}
                    {q.suggested_example && <div><p className="label-tiny mb-1">/example</p><p className="italic text-[var(--brand-muted)]">{q.suggested_example}</p></div>}
                  </div>
                </details>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(result.general_tips || []).length > 0 && (
              <div className="card-hard rounded-md p-5 bg-[var(--pastel-mint)]" data-testid="interview-tips">
                <p className="label-tiny mb-2 flex items-center gap-1.5"><GraduationCap size={12} weight="duotone"/> /general tips</p>
                <ul className="text-sm space-y-2">{result.general_tips.map((t, i) => <li key={`tip-${i}`} className="flex items-start gap-2"><CheckCircle size={14} weight="duotone" className="mt-0.5 shrink-0 text-[var(--bolt-sage)]"/>{t}</li>)}</ul>
              </div>
            )}
            {(result.gaps_to_address || []).length > 0 && (
              <div className="card-hard rounded-md p-5 bg-[var(--pastel-peach)]" data-testid="interview-gaps">
                <p className="label-tiny mb-2 flex items-center gap-1.5"><Warning size={12} weight="duotone"/> /gaps to address</p>
                <ul className="text-sm space-y-2">{result.gaps_to_address.map((t, i) => <li key={`gap-${i}`} className="flex items-start gap-2"><Warning size={14} weight="duotone" className="mt-0.5 shrink-0 text-[var(--bolt-terracotta)]"/>{t}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Section shells — CV, Cover Letter, Interview Prep
// ─────────────────────────────────────────────────────────────────────────────
const SUB_LABELS = {
  master: "Master ⭐",
  tailored: "Tailored 📄",
  scratch: "From Scratch",
  improve: "Improve / Review",
  saved: "Saved",
};

function SubNav({ subs, active, onChange, prefix }) {
  return (
    <div className="flex gap-2 flex-wrap mb-5" data-testid={`${prefix}-subnav`}>
      {subs.map((s) => (
        <button key={s} onClick={() => onChange(s)}
          data-testid={`${prefix}-sub-${s}`}
          aria-pressed={active === s}
          className={`btn-hard rounded-md px-3 py-1.5 text-xs font-mono-d ${active === s ? "bg-[var(--brand-ink)] text-white" : "bg-white"}`}>
          {SUB_LABELS[s] || s}
        </button>
      ))}
    </div>
  );
}

function CVSection({ sub, setSub }) {
  return (
    <div data-testid="cv-section">
      <SubNav subs={CV_SUBS} active={sub} onChange={setSub} prefix="cv"/>
      {sub === "master" && <MasterCV/>}
      {sub === "tailored" && <TailoredCV/>}
      {sub === "scratch" && <CVFromScratch/>}
      {sub === "improve" && <ImproveCV/>}
      {sub === "saved" && (
        <div>
          <p className="text-sm text-[var(--brand-muted)] mb-3">CVs generated using information stored in your vault. rename, promote to master, or delete.</p>
          <SavedList kinds={["cv_master", "cv_tailored"]} emptyLabel="no saved cvs yet — generate a master or tailored version to see it here."/>
        </div>
      )}
    </div>
  );
}

function CoverSection({ sub, setSub }) {
  return (
    <div data-testid="cover-section">
      <SubNav subs={COVER_SUBS} active={sub} onChange={setSub} prefix="cover"/>
      {sub === "master" && <MasterCoverLetter/>}
      {sub === "tailored" && <TailoredCoverLetter/>}
      {sub === "scratch" && <CLFromScratch/>}
      {sub === "improve" && <ImproveCoverLetter/>}
      {sub === "saved" && (
        <div>
          <p className="text-sm text-[var(--brand-muted)] mb-3">cover letters generated using information stored in your vault. rename, promote, or delete.</p>
          <SavedList kinds={["cover_letter_master", "cover_letter"]} emptyLabel="no saved cover letters yet."/>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────────────────────
const TILES = [
  { key: "cv",        icon: Target,           label: "CV",             blurb: "master, tailored, from scratch, review, saved — all in one." },
  { key: "cover",     icon: FileText,         label: "Cover Letter",   blurb: "master, tailored, from scratch, review, saved." },
  { key: "interview", icon: ChatTeardropDots, label: "Interview Prep", blurb: "ai coach · mock questions · STAR · role-specific." },
];

export default function VaultCoach() {
  const [params, setParams] = useSearchParams();
  const initialSection = SECTION_KEYS.includes(params.get("section")) ? params.get("section") : (SECTION_KEYS.includes(params.get("tab")) ? params.get("tab") : "cv");
  const [section, setSection] = useState(initialSection);
  const initialSub = params.get("sub") || (initialSection === "cv" ? "master" : initialSection === "cover" ? "master" : null);
  const [cvSub, setCvSub] = useState(initialSection === "cv" && CV_SUBS.includes(initialSub) ? initialSub : "master");
  const [coverSub, setCoverSub] = useState(initialSection === "cover" && COVER_SUBS.includes(initialSub) ? initialSub : "master");

  useEffect(() => {
    // Keep URL in sync for shareable deep-links.
    const next = { section };
    if (section === "cv") next.sub = cvSub;
    if (section === "cover") next.sub = coverSub;
    setParams(next, { replace: true });
  }, [section, cvSub, coverSub, setParams]);

  const currentSub = section === "cv" ? cvSub : section === "cover" ? coverSub : null;
  const setSub = section === "cv" ? setCvSub : section === "cover" ? setCoverSub : () => {};

  return (
    <AppShell title="Vault Coach">
      <AIConsentModal/>
      <p className="label-tiny mb-2">/vault coach</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-2">
        one vault. <span className="text-[var(--brand-primary)]">every document.</span>
      </h1>
      <p className="text-[var(--brand-ink)]/70 max-w-2xl mb-5">
        CV · cover letter · interview prep — every output pulls from your vault, every save lands in document safe.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="vault-coach-tiles">
        {TILES.map((t) => {
          const Icon = t.icon; const active = section === t.key;
          return (
            <button key={t.key} onClick={() => setSection(t.key)} data-testid={`tool-${t.key}`} aria-pressed={active}
              className={`card-hard rounded-md text-left p-5 ${active ? "bg-[var(--brand-ink)] text-white" : "bg-white"}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={20} weight="duotone"/>
                <p className="font-display text-xl font-semibold tracking-tight">{t.label}</p>
                {active && <span className="ml-auto label-tiny px-2 py-0.5 rounded-full bg-white text-[var(--brand-ink)]" style={{ letterSpacing: "0.15em" }}>active</span>}
              </div>
              <p className={`text-sm ${active ? "text-white/80" : "text-[var(--brand-muted)]"}`}>{t.blurb}</p>
            </button>
          );
        })}
      </div>

      {section === "cv" && <CVSection sub={cvSub} setSub={setCvSub}/>}
      {section === "cover" && <CoverSection sub={coverSub} setSub={setCoverSub}/>}
      {section === "interview" && <InterviewPrepPanel/>}
    </AppShell>
  );
}
